import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    loadIntake,
    IntakeLoadResponse,
} from '../services/intakeService';
import { getIntakeStrings, resolveFormLanguage } from '../translations/intake';
import IntakeWizard from '../components/intake/IntakeWizard';

type State =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'ready'; data: IntakeLoadResponse }
    | { status: 'submitted'; language: string; justSubmitted: boolean };

const IntakePage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [state, setState] = useState<State>({ status: 'loading' });

    useEffect(() => {
        // Great Vibes drives the SeaDent script logo. The marketing site loads
        // it conditionally via cookie consent — that layout is bypassed here,
        // so inject the stylesheet directly. Functional, not tracking.
        const HREF = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
        if (!document.querySelector(`link[data-intake-font="great-vibes"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = HREF;
            link.setAttribute('data-intake-font', 'great-vibes');
            document.head.appendChild(link);
        }

        if (!token) {
            setState({ status: 'error', message: 'Missing token' });
            return;
        }
        let cancelled = false;
        loadIntake(token)
            .then((data) => {
                if (cancelled) return;
                // Drive html lang from the appointment, but resolve RU → EN since
                // we don't translate medical strings to Russian yet.
                document.documentElement.lang = resolveFormLanguage(data.appointment.language);
                if (data.readOnly) {
                    setState({ status: 'submitted', language: data.appointment.language, justSubmitted: false });
                } else {
                    setState({ status: 'ready', data });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setState({ status: 'error', message: err?.message || 'error' });
            });
        return () => { cancelled = true; };
    }, [token]);

    // Pick strings best-effort while loading (defaults to DE).
    const fallbackStrings = getIntakeStrings(undefined);

    if (state.status === 'loading') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#f9f9f7] to-white px-6">
                <div className="h-12 w-12 rounded-full border-4 border-[#384860]/20 border-t-[#384860] animate-spin" />
                <p className="mt-4 text-[#384860]/70 text-sm">{fallbackStrings.meta.loading}</p>
            </div>
        );
    }

    if (state.status === 'error') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#f9f9f7] to-white px-6 text-center">
                <div className="max-w-md">
                    <div className="mx-auto h-16 w-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-semibold text-[#384860]">
                        {fallbackStrings.meta.error_link}
                    </h1>
                </div>
            </div>
        );
    }

    if (state.status === 'submitted') {
        const strings = getIntakeStrings(state.language);
        const title = state.justSubmitted ? strings.meta.success_title : strings.meta.already_submitted_title;
        const body = state.justSubmitted ? strings.meta.success_body : strings.meta.already_submitted_body;
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#f9f9f7] to-white px-6 py-10 text-center">
                <div className="max-w-md w-full">
                    {state.justSubmitted ? (
                        <div className="mx-auto mb-6 w-full max-w-sm overflow-hidden rounded-2xl shadow-lg ring-1 ring-[#384860]/10 bg-black">
                            <video
                                src="/intake-success.mp4"
                                autoPlay
                                muted
                                playsInline
                                loop
                                className="w-full h-auto"
                                aria-hidden
                            />
                        </div>
                    ) : (
                        <div className="mx-auto h-16 w-16 rounded-2xl bg-[#ff6f59]/10 flex items-center justify-center mb-4">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#e55a45" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                    )}
                    <h1 className="text-2xl font-semibold text-[#384860]">{title}</h1>
                    <p className="mt-3 text-[#384860]/70">{body}</p>
                </div>
            </div>
        );
    }

    // status === 'ready'
    const onSubmitted = () => setState({ status: 'submitted', language: state.data.appointment.language, justSubmitted: true });
    return (
        <IntakeWizard
            token={token!}
            appointment={state.data.appointment}
            schema={state.data.schema}
            initialForm={state.data.form}
            onSubmitted={onSubmitted}
        />
    );
};

export default IntakePage;
